import { BaseService } from "./BaseService";
import {
	AuthUser,
	AuthResponse,
	LoginCredentials,
	GoogleCredentials,
	EotpRequest,
	EotpVerify,
	ErrorResponse,
	SuccessResponse,
	BuildxConfig,
} from "../types/index";

/**
 * Authentication service for Buildx
 * Handles user authentication, login, signup, and user management
 * 
 * @example
 * ```typescript
 * const auth = buildx.auth();
 * 
 * // Login with username and password
 * const result = await auth.login({ username: 'user', password: 'pass' });
 * 
 * // Get current user
 * const user = await auth.getCurrentUser();
 * 
 * // Sign up new user
 * const newUser = await auth.signup({ username: 'newuser', password: 'pass' });
 * ```
 */
export class Auth {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * Set the access token
	 */
	setAccessToken(token: string | null): void {
		this.baseService.setAccessToken(token);
	}

	/**
	 * Get the access token
	 */
	getAccessToken(): string | null {
		return this.baseService.getAccessToken();
	}

	/**
	 * Set the refresh token
	 */
	setRefreshToken(token: string | null): void {
		this.baseService.setRefreshToken(token);
	}

	/**
	 * Get the refresh token
	 */
	getRefreshToken(): string | null {
		return this.baseService.getRefreshToken();
	}

	/**
	 * Clear all stored tokens
	 */
	clearTokens(): void {
		this.baseService.clearTokens();
	}

	/**
	 * Check if user is authenticated
	 */
	isAuthenticated(): boolean {
		return this.baseService.isAuthenticated();
	}

	/**
	 * Store tokens from authentication response
	 */
	private storeTokens(response: AuthResponse | AuthUser): void {
		if ("access_token" in response && response.access_token) {
			this.baseService.setAccessToken(response.access_token);
		}
		if ("refresh_token" in response && response.refresh_token) {
			this.baseService.setRefreshToken(response.refresh_token);
		}
	}

	/**
	 * Check if response is an error response
	 */
	private isErrorResponse(response: any): response is ErrorResponse {
		return response && typeof response === "object" && "success" in response && response.success === false;
	}

	/**
	 * Login with username and password
	 */
	async login(credentials: LoginCredentials, projectId?: string): Promise<AuthUser | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/login");
		const response = await this.baseService.post<AuthResponse | AuthUser | ErrorResponse>(url, credentials);

		// Check if it's an error response
		if (this.isErrorResponse(response)) {
			return response;
		}

		// Store tokens if they exist in the response
		this.storeTokens(response as AuthResponse | AuthUser);
		return response as AuthUser;
	}

	/**
	 * Login with Google credentials
	 */
	async loginWithGoogle(credentials: GoogleCredentials, projectId?: string): Promise<AuthUser | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/google");
		const response = await this.baseService.post<AuthResponse | AuthUser | ErrorResponse>(url, credentials);

		// Check if it's an error response
		if (this.isErrorResponse(response)) {
			return response;
		}

		// Store tokens if they exist in the response
		this.storeTokens(response as AuthResponse | AuthUser);
		return response as AuthUser;
	}

	/**
	 * Sign up a new user
	 */
	async signup(credentials: LoginCredentials, projectId?: string): Promise<AuthUser | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/signup");
		const response = await this.baseService.post<AuthResponse | AuthUser | ErrorResponse>(url, credentials);

		// Check if it's an error response
		if (this.isErrorResponse(response)) {
			return response;
		}

		// Store tokens if they exist in the response
		this.storeTokens(response as AuthResponse | AuthUser);
		return response as AuthUser;
	}

	/**
	 * Request email OTP for authentication
	 */
	async requestEotp(request: EotpRequest, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/eotp/auth/request");
		return await this.baseService.post(url, request);
	}

	/**
	 * Verify email OTP
	 */
	async verifyEotp(verify: EotpVerify, projectId?: string): Promise<AuthUser | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/eotp/auth/verify");
		const response = await this.baseService.post<AuthResponse | AuthUser | ErrorResponse>(url, verify);

		// Check if it's an error response
		if (this.isErrorResponse(response)) {
			return response;
		}

		// Store tokens if they exist in the response
		this.storeTokens(response as AuthResponse | AuthUser);
		return response as AuthUser;
	}

	/**
	 * Get current authenticated user
	 */
	async getCurrentUser(projectId?: string): Promise<AuthUser | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/me");
		return await this.baseService.get(url);
	}

	/**
	 * Refresh authentication token
	 */
	async refreshToken(projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/token");
		const response = await this.baseService.post<AuthResponse | SuccessResponse | ErrorResponse>(url, {});

		// Check if it's an error response
		if (this.isErrorResponse(response)) {
			return response;
		}

		// Store tokens if they exist in the response
		if ("access_token" in response && response.access_token) {
			this.storeTokens(response as AuthResponse);
		}

		return response as SuccessResponse;
	}

	/**
	 * Update user password
	 */
	async updatePassword(password: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/password/update");
		return await this.baseService.post(url, { password });
	}

	/**
	 * Admin update user password
	 */
	async adminUpdatePassword(userId: string, password: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/auth/password/update");
		return await this.baseService.post(url, { user_id: userId, password });
	}

	/**
	 * Update push notification token
	 */
	async updatePushToken(token: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/users/push-tokens");
		return await this.baseService.post(url, { token });
	}

	/**
	 * List all users (admin only)
	 */
	async listUsers(organizationId?: string): Promise<AuthUser[] | ErrorResponse> {
		const url = organizationId
			? this.baseService.buildOrgUrl(organizationId, "/auth/users")
			: "/auth/users";
		return await this.baseService.get(url);
	}

	/**
	 * Lookup users (for autocomplete/search)
	 */
	async lookupUsers(organizationId?: string): Promise<AuthUser[] | ErrorResponse> {
		const url = organizationId
			? this.baseService.buildOrgUrl(organizationId, "/auth/users/lookup")
			: "/auth/users/lookup";
		return await this.baseService.get(url);
	}

	/**
	 * Get user by ID
	 */
	async getUser(userId: string, organizationId?: string): Promise<AuthUser | ErrorResponse> {
		const url = organizationId
			? this.baseService.buildOrgUrl(organizationId, `/auth/user/${userId}`)
			: `/auth/user/${userId}`;
		return await this.baseService.get(url);
	}

	/**
	 * Delete user (admin only)
	 */
	async deleteUser(userId: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/auth/user/${userId}/delete`);
		return await this.baseService.post(url, {});
	}
} 